/**
 * Whole-site acreage claims for the generation, frozen-source and retry checks.
 * This is deliberately a conservative English-prose recognizer, not a locale
 * guesser or a land-use classifier. A subset or ambiguous scope is not evidence
 * of the site's total area. No source values or units are inferred or changed.
 */

/** Accept decimal points and correctly grouped thousands commas only. */
export function parseAcreageNumber(token: string): number | null {
  const normalized = token.replace(/^−/, '-');
  if (!/^[+-]?(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?$/.test(normalized)) return null;
  const value = Number(normalized.replace(/,/g, ''));
  return Number.isFinite(value) ? value : null;
}

const WHOLE_AREA = String.raw`(?:(?:total|gross|overall|selected)\s+)?(?:site|parcel|property|study\s+area)(?:['’]s)?(?:\s+(?:(?:total|gross|overall)\s+)?(?:area|acreage|size))?`;
const TOTAL_AREA = String.raw`(?:total|gross|overall)\s+(?:(?:site|parcel|property|selected)\s+)?(?:area|acreage)`;
const APPROXIMATION = String.raw`(?:(?:approximately|about|roughly|around)\s+)?`;
const BEFORE_AREA = new RegExp(
  String.raw`\b(?:${WHOLE_AREA}|${TOTAL_AREA})\s*(?:(?:is|has|comprises|encompasses|covers|spans|totals|measures|of|at|:|=)\s*)?(?:a\s+total\s+of\s+)?${APPROXIMATION}$`, 'i',
);
const AFTER_AREA = new RegExp(String.raw`^\s+(?:${WHOLE_AREA})\b`, 'i');
const SUBSET = /\b(?:flood\s*plain|floodway|wetlands?|developable|buildable|usable|net|constrained|unconstrained|buffers?|easements?|habitat|woodlands?|forest|excluded|exclusion|mitigation|disturbed)\b/i;

export function extractSiteAreaClaimsAcres(text: string): number[] {
  const values = new Set<number>();
  // JSON string boundaries and escaped newlines must not lend site context to
  // an unrelated field. Keep decimal points inside their numeric tokens.
  const clauses = text.replace(/\\[nr]/g, '\n').split(/[.!?](?!\d)|[\n;|"{}\[\]]+/);
  for (const clause of clauses) {
    // A selected parcel within a separately quantified parent site names two
    // different footprints. A scalar SED acreage cannot establish which one
    // the prose refers to; do not silently equate either subject with it.
    if (/\b(?:within|inside|part\s+of)\s+(?:(?:the|a)\s+)?(?:(?:larger|parent|overall)\s+)?[+−-]?\d[\d,.]*\s*-?\s*(?:acres?|ac)\s+(?:site|parcel|property|study\s+area)\b/i.test(clause)) continue;
    const mentions = clause.matchAll(/(?<![\p{L}\p{N}.,+−-])([+−-]?\d[\d,.]*)\s*(?:-\s*)?(?:(total)\s+)?(acres?|ac)\b/giu);
    for (const match of mentions) {
      const value = parseAcreageNumber(match[1]);
      if (value === null) continue;
      const before = clause.slice(0, match.index);
      const after = clause.slice(match.index + match[0].length);
      // Acre-feet is a volume, not an area; area-per-unit is not site area.
      if (/^\s*(?:-?\s*(?:feet|foot|ft)\b|\/|per\b)/i.test(after)) continue;
      // Do not salvage the tail of unsupported space-grouped numbers/ranges.
      if (/[\d.,]\s+$/.test(before) || /\d\s*[-–—]\s*$/.test(before)) continue;

      const directBefore = before.match(BEFORE_AREA);
      const directAfter = after.match(AFTER_AREA);
      const localBefore = before.split(/[,;]|\b(?:but|whereas|while|and)\b/i).at(-1) ?? '';
      if (directAfter && /\b(?:not|never|no|at\s+(?:least|most)|up\s+to|more\s+than|less\s+than)\b/i.test(localBefore)) continue;
      if (directAfter && /\b(?:includes?|contains?|including)\s+(?:(?:a|an|one|the)\s+)?$/i.test(before)) continue;
      // "355.06 acres of floodplain within 1,170 total acres" has a clear
      // denominator, but "the site includes 355.06 acres" alone does not.
      const totalDenominator = Boolean(match[2])
        && /\b(?:within|out\s+of)\s*$/i.test(before)
        && /\b(?:site|parcel|property|study\s+area)\b/i.test(before);
      if (!directBefore && !directAfter && !totalDenominator) continue;

      // A total/developable-area phrase still describes a subset. Likewise a
      // quantity followed by "of wetlands" is not the whole site's acreage.
      const scopeStart = directBefore?.index;
      const leadingScope = scopeStart === undefined ? '' : before.slice(0, scopeStart);
      if (SUBSET.test(leadingScope.trim().split(/\s+/).slice(-2).join(' '))) continue;
      if (/\b(?:of|on|within|at|across|inside|throughout|under|over)\s+(?:the\s+)?$/i.test(leadingScope) && SUBSET.test(leadingScope)) continue;
      if (/\b(?:part|portion|subset|section|fraction|some|most|remainder)\s+of\s+(?:the\s+)?$/i.test(leadingScope)) continue;
      const trailingScope = after.match(/^\s+(?:of|in|within|for|is|are)\s+([\p{L}\p{N}\s%-]{1,60})/iu)?.[1];
      if (trailingScope && SUBSET.test(trailingScope)) continue;
      if (/^\s+(?:available|remaining|developable|buildable|usable)\b/i.test(after)) continue;
      const parentheticalScope = after.match(/^\s*\(([^)]*)\)/)?.[1];
      if (parentheticalScope && SUBSET.test(parentheticalScope)) continue;
      if (directAfter && SUBSET.test(after.slice(directAfter[0].length).trim().split(/\s+/).slice(0, 2).join(' '))) continue;
      values.add(value);
    }
  }
  return [...values];
}

/** One metric check, including every clearly scoped claim so a later mismatch
 * cannot hide behind an earlier matching value. Unknown is never coerced to 0. */
export function checkSiteAreaClaims(text: string, sourceAcres: unknown): {
  checked: number; verified: number; contradictions: string[];
} {
  if (typeof sourceAcres !== 'number' || !Number.isFinite(sourceAcres) || sourceAcres < 0) {
    return { checked: 0, verified: 0, contradictions: [] };
  }
  const claims = extractSiteAreaClaimsAcres(text);
  if (claims.length === 0) return { checked: 0, verified: 0, contradictions: [] };
  // Keep the existing 10-acre tolerance; negative claims are nonphysical even
  // when an explicit zero source would otherwise bring them within tolerance.
  const mismatch = claims.find(value => value < 0 || Math.abs(value - sourceAcres) > 10);
  return mismatch === undefined
    ? { checked: 1, verified: 1, contradictions: [] }
    : { checked: 1, verified: 0, contradictions: [`Site area: AI says ${mismatch}, SED has ${sourceAcres}`] };
}
